import { uiSurface } from "shared/ui/uiSurface"
import { Add, Delete } from "@mui/icons-material"
import {
  Box,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import type { PhoneEntry } from "./useOrderCreateForm"

const PHONE_LABELS = ["Cell", "Home", "Work", "Fax", "Other"]

type Props = {
  phoneNumbers: PhoneEntry[]
  onSetPhone: (index: number, field: keyof PhoneEntry, value: string) => void
  onAddPhone: () => void
  onRemovePhone: (index: number) => void
}

export const PhoneNumberList: FC<Props> = ({
  phoneNumbers,
  onSetPhone,
  onAddPhone,
  onRemovePhone,
}) => {
  const { t } = useTranslation()

  return (
    <Box {...uiSurface("UI-DLV2-037")}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
        {t("orderCreate.phoneNumbers")}
      </Typography>
      <Stack spacing={1}>
        {phoneNumbers.map((phone, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              value={phone.label}
              onChange={(e) => onSetPhone(idx, "label", e.target.value)}
              sx={{ minWidth: 100 }}
              data-testid={`order-create-phone-label-${idx}`}
            >
              {PHONE_LABELS.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              fullWidth
              placeholder={t("orderCreate.phoneNumber")}
              value={phone.number}
              onChange={(e) => onSetPhone(idx, "number", e.target.value)}
              data-testid={`order-create-phone-number-${idx}`}
            />
            <IconButton
              size="small"
              onClick={() => onRemovePhone(idx)}
              data-testid={`order-create-phone-remove-${idx}`}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Link
        component="button"
        variant="caption"
        onClick={onAddPhone}
        sx={{ mt: 0.5, display: "inline-flex", alignItems: "center", gap: 0.5 }}
        data-testid="order-create-phone-add"
      >
        <Add sx={{ fontSize: 14 }} /> {t("orderCreate.addPhone")}
      </Link>
    </Box>
  )
}
